import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Delay before the outline/toolbar are cleared after the cursor leaves a row
 * or its toolbar. Long enough that crossing the small gap from a row up into
 * its floating toolbar doesn't blink things out, short enough that a genuine
 * leave still feels immediate. This window is also what keeps a shown toolbar
 * reachable while the cursor transits the row above it.
 */
export const PRESENTATION_HOVER_LEAVE_DELAY_MS = 120;

/**
 * Named hover-intent delays before a row's floating toolbar is displayed. The
 * blue outline follows the cursor with no delay; only the toolbar waits, so
 * quick passes over rows don't flash toolbars and the toolbar appears once the
 * cursor settles on a row. Pass one to `PresentationHoverActionsProvider`'s
 * `toolbarDisplayDelayMS` instead of a magic number.
 */
export const TOOLBAR_DISPLAY_DELAY_100MS = 100;
export const TOOLBAR_DISPLAY_DELAY_175MS = 175;
export const TOOLBAR_DISPLAY_DELAY_250MS = 250;

/** Used when a provider doesn't specify a `toolbarDisplayDelayMS`. */
const DEFAULT_TOOLBAR_DISPLAY_DELAY_MS = TOOLBAR_DISPLAY_DELAY_250MS;

export interface PresentationHoverActionsContextValue {
  /**
   * True while hover treatments are suppressed — e.g. the chart is still
   * measuring/re-scaling (not ready). Consumers should render no outline or
   * toolbar; existing hover state is cleared when this flips on.
   */
  disabled: boolean;
  /**
   * Row currently under the cursor. Drives the blue outline and updates with
   * no delay so the highlight tracks the mouse.
   */
  outlineId: string | null;
  /**
   * Row whose floating toolbar is shown. Lags `outlineId` by the hover-intent
   * delay, so the toolbar only appears after the cursor settles.
   */
  toolbarId: string | null;
  /**
   * A row was entered: outline it immediately and schedule its toolbar after
   * the provider's `toolbarDisplayDelayMS`.
   */
  setHovered: (id: string) => void;
  /**
   * A row or the toolbar was left: clear the outline and toolbar after the
   * leave debounce.
   */
  scheduleClearHover: () => void;
  /**
   * The floating toolbar was entered: keep it shown and snap the outline to
   * its row (so the highlight matches while the toolbar is in use).
   */
  cancelClearHover: () => void;
  /**
   * Clear the outline and toolbar immediately (no leave debounce). Used after
   * a toolbar action runs — selecting a menu item or clicking a button — so the
   * toolbar doesn't linger over the row once the user has acted.
   */
  dismissHover: (options?: { suppressChartHover?: boolean }) => void;
  /**
   * True after a toolbar action dismissed hover, until the cursor makes a fresh
   * entry onto the chart. Keeps the chart-level "Edit diagram" CTA from flashing
   * over the background: clicking a node's toolbar clears the row hover, and the
   * whole diagram is briefly "hovered" while a launched drawer/modal animates in
   * — without this the chart CTA would pop up in that gap.
   */
  chartHoverSuppressed: boolean;
  /** Clear {@link chartHoverSuppressed} — a genuine new chart hover. */
  clearChartHoverSuppression: () => void;
}

/**
 * Null when no provider is mounted. `PresentationHoverRow` reads this
 * context optionally: outside a presentation there's no provider, so the
 * row renders its children plainly with no blue hover treatment. This is
 * how the same table component stays un-decorated everywhere except inside
 * a presentation.
 */
const PresentationHoverActionsContext =
  createContext<PresentationHoverActionsContextValue | null>(null);

/**
 * Tracks the hovered row for a presentation. The outline follows the cursor
 * instantly; the toolbar appears after a hover-intent delay and stays
 * reachable while the cursor crosses the row above it on the way up. Wrap the
 * part of a presentation that should get the blue hover-action treatment in
 * this provider; rows rendered without it stay plain.
 */
interface PresentationHoverActionsProviderProps {
  /**
   * How long the cursor must settle on a row before its toolbar appears. Use a
   * `TOOLBAR_DISPLAY_DELAY_*MS` constant. Defaults to 250ms.
   */
  toolbarDisplayDelayMS?: number;
  /**
   * Suppress all hover treatments while true — e.g. the chart isn't ready to
   * scale (still measuring/re-laying-out), so toolbars/outlines shouldn't show
   * over it. Any active hover is cleared when this flips on.
   */
  disabled?: boolean;
}

export function PresentationHoverActionsProvider({
  children,
  toolbarDisplayDelayMS = DEFAULT_TOOLBAR_DISPLAY_DELAY_MS,
  disabled = false,
}: PropsWithChildren<PresentationHoverActionsProviderProps>) {
  const [outlineId, setOutlineId] = useState<string | null>(null);
  const [toolbarId, setToolbarId] = useState<string | null>(null);
  const [chartHoverSuppressed, setChartHoverSuppressed] = useState(false);
  // Refs mirror state/props for synchronous reads inside the timer callbacks.
  const toolbarIdRef = useRef<string | null>(null);
  const showDelayRef = useRef(toolbarDisplayDelayMS);
  showDelayRef.current = toolbarDisplayDelayMS;
  // Read inside setHovered (a stable callback) to ignore hovers while disabled.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const outlineClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toolbarClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toolbarShowRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Row whose toolbar should appear once the currently shown toolbar finishes
  // clearing — so only one toolbar is ever visible at a time.
  const pendingShowIdRef = useRef<string | null>(null);

  const clearTimer = useCallback(
    (ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
      if (ref.current !== null) {
        clearTimeout(ref.current);
        ref.current = null;
      }
    },
    []
  );

  const applyToolbar = useCallback((id: string | null) => {
    toolbarIdRef.current = id;
    setToolbarId(id);
  }, []);

  const scheduleToolbarShow = useCallback(
    (id: string) => {
      clearTimer(toolbarShowRef);
      toolbarShowRef.current = setTimeout(() => {
        toolbarShowRef.current = null;
        applyToolbar(id);
      }, showDelayRef.current);
    },
    [applyToolbar, clearTimer]
  );

  const setHovered = useCallback(
    (id: string) => {
      // Ignore hovers while suppressed (e.g. chart not ready to scale).
      if (disabledRef.current) return;
      // Outline tracks the cursor's row immediately.
      clearTimer(outlineClearRef);
      setOutlineId(id);

      // Already this row's toolbar — keep it; nothing to schedule.
      if (id === toolbarIdRef.current) {
        clearTimer(toolbarClearRef);
        clearTimer(toolbarShowRef);
        pendingShowIdRef.current = null;
        return;
      }

      // Another row's toolbar is still shown and on its way out via the leave
      // debounce. Leave it visible (so it stays reachable) and queue this row's
      // toolbar to appear once that one has cleared — only one toolbar is ever
      // shown at a time. The clear timer kicks off the queued show when it fires.
      if (toolbarIdRef.current !== null && toolbarClearRef.current !== null) {
        clearTimer(toolbarShowRef);
        pendingShowIdRef.current = id;
        return;
      }

      // Nothing in the way — schedule this row's toolbar after the hover delay.
      pendingShowIdRef.current = null;
      scheduleToolbarShow(id);
    },
    [clearTimer, scheduleToolbarShow]
  );

  const scheduleClearHover = useCallback(() => {
    // A pending toolbar show for the row we're leaving is moot.
    clearTimer(toolbarShowRef);

    clearTimer(outlineClearRef);
    outlineClearRef.current = setTimeout(() => {
      outlineClearRef.current = null;
      setOutlineId(null);
    }, PRESENTATION_HOVER_LEAVE_DELAY_MS);

    clearTimer(toolbarClearRef);
    toolbarClearRef.current = setTimeout(() => {
      toolbarClearRef.current = null;
      applyToolbar(null);
      // The cursor settled on another row while this toolbar was clearing —
      // now that it's gone, show that row's toolbar (one toolbar at a time).
      if (pendingShowIdRef.current !== null) {
        const nextId = pendingShowIdRef.current;
        pendingShowIdRef.current = null;
        scheduleToolbarShow(nextId);
      }
    }, PRESENTATION_HOVER_LEAVE_DELAY_MS);
  }, [applyToolbar, clearTimer, scheduleToolbarShow]);

  const cancelClearHover = useCallback(() => {
    clearTimer(outlineClearRef);
    clearTimer(toolbarClearRef);
    clearTimer(toolbarShowRef);
    pendingShowIdRef.current = null;
    // Snap the outline to the toolbar's row so the highlight matches while the
    // toolbar is being used.
    setOutlineId(toolbarIdRef.current);
  }, [clearTimer]);

  const dismissHover = useCallback(
    ({ suppressChartHover = true }: { suppressChartHover?: boolean } = {}) => {
      pendingShowIdRef.current = null;
      clearTimer(outlineClearRef);
      clearTimer(toolbarClearRef);
      clearTimer(toolbarShowRef);
      setOutlineId(null);
      applyToolbar(null);
      // A toolbar action dismissed hover: suppress the chart CTA until the
      // cursor re-enters the chart, so it doesn't flash while the launched
      // drawer/modal animates over the diagram. (Skipped when the dismiss is
      // driven by the chart going not-ready, which suppresses the CTA anyway.)
      if (suppressChartHover) {
        setChartHoverSuppressed(true);
      }
    },
    [applyToolbar, clearTimer]
  );

  const clearChartHoverSuppression = useCallback(() => {
    setChartHoverSuppressed(false);
  }, []);

  // Clean up pending timers if the provider unmounts mid-debounce.
  useEffect(
    () => () => {
      clearTimer(outlineClearRef);
      clearTimer(toolbarClearRef);
      clearTimer(toolbarShowRef);
    },
    [clearTimer]
  );

  // Clear any active hover the moment treatments are suppressed, so a toolbar
  // or outline showing when the chart starts re-measuring doesn't linger.
  useEffect(() => {
    if (disabled) {
      // The chart is already suppressed via `disabled`; don't also arm the
      // post-action suppression, or the CTA would stay hidden after re-ready
      // until a fresh hover.
      dismissHover({ suppressChartHover: false });
    }
  }, [disabled, dismissHover]);

  const value = useMemo<PresentationHoverActionsContextValue>(
    () => ({
      disabled,
      outlineId: disabled ? null : outlineId,
      toolbarId: disabled ? null : toolbarId,
      setHovered,
      scheduleClearHover,
      cancelClearHover,
      dismissHover,
      chartHoverSuppressed,
      clearChartHoverSuppression,
    }),
    [
      disabled,
      outlineId,
      toolbarId,
      setHovered,
      scheduleClearHover,
      cancelClearHover,
      dismissHover,
      chartHoverSuppressed,
      clearChartHoverSuppression,
    ]
  );

  return (
    <PresentationHoverActionsContext.Provider value={value}>
      {children}
    </PresentationHoverActionsContext.Provider>
  );
}

/**
 * Returns the hover-actions context, or null when not inside a
 * `PresentationHoverActionsProvider`. Components use the null result to
 * fall back to plain, undecorated rendering outside of presentations.
 */
export function usePresentationHoverActionsContext(): PresentationHoverActionsContextValue | null {
  return useContext(PresentationHoverActionsContext);
}
